import {
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

import { readClaims } from "./claims.js";
import {
  cognitoFailure,
  confirmationFailureMessage,
  isHiddenAccountFailure,
  resetFailureMessage,
} from "./account-errors.js";
import { config } from "../config.js";

/**
 * Signing in, signing out, and knowing who is signed in.
 *
 * **SRP, not a password sent to the server.** The web client enables
 * `userSrp` and, outside production, `adminUserPassword` — the second exists
 * only so `smoke:auth` and `test:cross-tenant` can obtain a token, and the
 * browser must never use it. `amazon-cognito-identity-js` does the SRP exchange
 * client-side, so the password never leaves the device.
 *
 * **The session lives in `localStorage`, which is the library's default and is
 * a deliberate acceptance rather than an oversight.** A token readable by
 * script is a token an XSS bug can steal. The alternative that actually fixes
 * that is an httpOnly cookie issued by a back end that holds the refresh token,
 * which is a different authentication design and a larger change than a
 * milestone that is trying to make sign-in exist at all. What keeps the blast
 * radius bounded meanwhile: the id token lasts an hour, it carries no tenant
 * the user is not entitled to, and every route re-verifies it. Recorded in
 * `docs/ARCHITECTURE.md` as open rather than settled.
 */

const pool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.webClientId,
});

export interface Session {
  /** The raw id token. The only thing the API accepts. */
  readonly idToken: string;
  readonly email: string;
  /**
   * From the token's claims, and **for display only**.
   *
   * Nothing in the browser verifies this signature, so none of it may decide
   * what a user is allowed to do — it decides what to *render*. Every actual
   * refusal happens at the API, where the JWT authorizer verifies the token and
   * `withTenantPath` compares the path against the claim. A front end that
   * hides a button is a courtesy; the 403 is the control.
   */
  readonly tenantId: string | undefined;
  readonly tenantStatus: string | undefined;
}

const toSession = (cognitoSession: CognitoUserSession): Session => {
  const idToken = cognitoSession.getIdToken().getJwtToken();
  const claims = readClaims(idToken);

  return {
    idToken,
    email: claims.email ?? "",
    tenantId: claims.tenantId,
    tenantStatus: claims.tenantStatus,
  };
};

/**
 * The session already in this browser, refreshed if it has expired.
 *
 * `getSession` uses the refresh token when the id token has aged out, which is
 * why this is the right thing to call before every request rather than caching
 * a token in a variable at sign-in and using it for an hour.
 */
export const currentSession = async (): Promise<Session | undefined> => {
  const user = pool.getCurrentUser();
  if (!user) return undefined;

  return new Promise<Session | undefined>((resolve) => {
    user.getSession((error: Error | null, result: CognitoUserSession | null) => {
      if (error || !result?.isValid()) {
        resolve(undefined);
        return;
      }
      resolve(toSession(result));
    });
  });
};

/** Raised for the cases a sign-in form has to say something specific about. */
export class SignInError extends Error {
  constructor(
    message: string,
    /** True when Cognito wants something this shell cannot yet collect. */
    readonly unsupportedChallenge = false,
    readonly needsConfirmation = false,
  ) {
    super(message);
    this.name = "SignInError";
  }
}

export const signIn = async (
  email: string,
  password: string,
): Promise<Session> => {
  const user = new CognitoUser({ Username: email, Pool: pool });

  return new Promise<Session>((resolve, reject) => {
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (result) => resolve(toSession(result)),
        onFailure: (error: unknown) => {
          const raw = error as { code?: string; message?: string };
          /*
           * `preventUserExistenceErrors` is on, so Cognito returns the same
           * `NotAuthorizedException` for a wrong password and an account that
           * does not exist. Saying "incorrect email or password" is therefore
           * accurate rather than coy — and repeating Cognito's own wording
           * would leak the distinction the pool setting exists to hide.
           */
          if (raw.code === "NotAuthorizedException") {
            reject(new SignInError("Incorrect email or password."));
            return;
          }
          if (raw.code === "UserNotConfirmedException") {
            reject(
              new SignInError(
                "This account has not been confirmed yet. Check your email " +
                  "for the confirmation code.",
                false,
                true,
              ),
            );
            return;
          }
          reject(new SignInError(raw.message ?? "Sign-in failed."));
        },
        // A new-password or MFA challenge needs a second form this shell does
        // not have. Failing with a specific message beats hanging on a promise
        // that never settles, which is what omitting these callbacks does.
        newPasswordRequired: () =>
          reject(
            new SignInError(
              "This account must set a new password before signing in, " +
                "which this shell cannot do yet.",
              true,
            ),
          ),
        mfaRequired: () =>
          reject(
            new SignInError(
              "This account has MFA enabled, which this shell cannot do yet.",
              true,
            ),
          ),
        totpRequired: () =>
          reject(
            new SignInError(
              "This account has MFA enabled, which this shell cannot do yet.",
              true,
            ),
          ),
      },
    );
  });
};

export class AccountFlowError extends Error {
  constructor(
    message: string,
    /** Confirmation succeeded but no usable password was available. */
    readonly needsPasswordReset = false,
  ) {
    super(message);
    this.name = "AccountFlowError";
  }
}

export interface ConfirmationAttempt {
  readonly email: string;
  /** Kept in React memory only so confirmation can sign in directly. */
  readonly password: string;
}

const cognitoUser = (email: string): CognitoUser =>
  new CognitoUser({ Username: email, Pool: pool });

export const startSignUp = async (
  email: string,
  password: string,
): Promise<ConfirmationAttempt> => {
  const normalizedEmail = email.trim();
  let existingAccount = false;

  await new Promise<void>((resolve, reject) => {
    pool.signUp(
      normalizedEmail,
      password,
      [new CognitoUserAttribute({ Name: "email", Value: normalizedEmail })],
      [],
      (error) => {
        if (!error) {
          resolve();
          return;
        }

        if (cognitoFailure(error).code === "UsernameExistsException") {
          // Do not strand an earlier unconfirmed signup. The confirmation
          // screen can resend; a confirmed account receives nothing and can
          // use the reset path, without this response saying which it was.
          existingAccount = true;
          resolve();
          return;
        }

        const failure = cognitoFailure(error);
        if (failure.code === "InvalidPasswordException") {
          reject(
            new AccountFlowError(
              "Use at least 12 characters with upper-case, lower-case and a number.",
            ),
          );
          return;
        }
        reject(new AccountFlowError("We could not start signup. Please try again."));
      },
    );
  });

  if (existingAccount) {
    await resendConfirmationCode(normalizedEmail);
  }

  return { email: normalizedEmail, password };
};

export const resendConfirmationCode = async (email: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    cognitoUser(email).resendConfirmationCode((error) => {
      if (
        !error ||
        isHiddenAccountFailure(error) ||
        cognitoFailure(error).code === "InvalidParameterException"
      ) {
        resolve();
        return;
      }
      reject(
        new AccountFlowError(
          cognitoFailure(error).code === "LimitExceededException"
            ? "Too many attempts. Wait a little before requesting another code."
            : "We could not resend the code. Please try again.",
        ),
      );
    });
  });

export const confirmSignUpAndSignIn = async (
  attempt: ConfirmationAttempt,
  code: string,
): Promise<Session> => {
  await new Promise<void>((resolve, reject) => {
    cognitoUser(attempt.email).confirmRegistration(code.trim(), false, (error) => {
      if (error) {
        reject(new AccountFlowError(confirmationFailureMessage(error)));
        return;
      }
      resolve();
    });
  });

  try {
    return await signIn(attempt.email, attempt.password);
  } catch {
    // This happens when an abandoned signup was restarted with the same
    // address but a different password. The address is no longer hostage: the
    // person can immediately establish a password through the email they just
    // proved, without an operator deleting the account.
    throw new AccountFlowError(
      "Your email is confirmed. Set a new password to continue.",
      true,
    );
  }
};

export const requestPasswordReset = async (email: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (): void => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };
    cognitoUser(email).forgotPassword({
      inputVerificationCode: finish,
      onSuccess: finish,
      onFailure: (error) => {
        if (isHiddenAccountFailure(error)) {
          finish();
          return;
        }
        reject(
          new AccountFlowError(
            cognitoFailure(error).code === "LimitExceededException"
              ? "Too many attempts. Wait a little before trying again."
              : "We could not request a reset. Please try again.",
          ),
        );
      },
    });
  });

export const confirmPasswordReset = async (
  email: string,
  code: string,
  newPassword: string,
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    cognitoUser(email).confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (error) =>
        reject(new AccountFlowError(resetFailureMessage(error))),
    });
  });

/**
 * `ACC-004-AC3`. `signOut` clears the stored tokens; it does not revoke the
 * refresh token at Cognito, which `globalSignOut` would — that needs a valid
 * session and is worth adding when there is a reason to invalidate other
 * devices.
 */
export const signOut = (): void => {
  pool.getCurrentUser()?.signOut();
};
